import { useEffect } from "react";
import { useDemos } from "@/hooks/useDemos";
import { useResult } from "@/hooks/useResult";
import { Dossier } from "@/components/Dossier/Dossier";
import { getCaseCopy } from "@/content";
import type { TocEntry } from "@/components/DossierTOC/DossierTOC";

type Props = { slug: string };

export function DemoRoute({ slug }: Props) {
  const copy = getCaseCopy(slug);
  const demos = useDemos();
  const demo = demos.data?.find((d) => d.slug === slug);
  const result = useResult(demo?.event_log_id);

  useEffect(() => {
    if (copy?.displayName) {
      document.title = `${copy.displayName} — FlowLens`;
    } else if (demo?.display_name) {
      document.title = `${demo.display_name} — FlowLens`;
    }
  }, [copy?.displayName, demo?.display_name]);

  if (!copy) {
    return (
      <main className="main-col">
        <NotInPortfolio slug={slug} />
      </main>
    );
  }
  if (demos.isLoading) return <main className="main-col"><LoadingState copy={copy.displayName} /></main>;
  if (!demo) return <main className="main-col"><BackendMissing slug={slug} /></main>;
  if (result.isLoading) return <main className="main-col"><LoadingState copy={copy.displayName} /></main>;
  if (result.error || !result.data) return <main className="main-col"><ErrorPanel err={result.error} /></main>;

  return <Dossier copy={copy} result={result.data} />;
}

/* Static TOC derived from the authored CaseCopy — does not depend on
   backend data so the left rail can render before /result resolves. */
export function tocForSlug(slug: string | undefined): TocEntry[] {
  const copy = slug ? getCaseCopy(slug) : undefined;
  if (!copy) return [];
  const heroTail = copy.hero.kicker.replace(/^Issue\s+[IVX]+\s*·\s*/i, "").trim();
  return [
    { id: "sec-note", label: "A note from the analyst" },
    { id: "sec-hero", label: `Issue ${copy.issueNumberRoman} · ${heroTail}` },
    { id: "sec-02",   label: copy.sectionTwo.label },
    { id: "sec-03",   label: findingsTocLabel(copy) },
    { id: "sec-04",   label: "· 04 · How this was built · Stack + method" },
    { id: "sec-05",   label: "· 05 · As a consultant, I'd recommend" },
  ];
}

function findingsTocLabel(copy: ReturnType<typeof getCaseCopy> & {}): string {
  /* The TOC does not know findings.length before /result resolves. Strip
     the leading number word ("four", "six", "zero", etc) produced by
     labelFromCount to get a count-agnostic label. */
  return copy.sectionThree
    .labelFromCount(0)
    .replace(/^(·\s*03\s*·\s*)\w+\s+/, "$1");
}

function LoadingState({ copy }: { copy: string }) {
  return (
    <div style={{ paddingTop: 48 }}>
      <p className="caps" style={{ marginBottom: 24, color: "var(--ink-3)" }}>
        Retrieving dossier…
      </p>
      <h1 style={{
        fontFamily: "var(--font-display)",
        fontStyle: "italic",
        fontSize: "clamp(38px, 6vw, 72px)",
        color: "var(--ink-40)",
        lineHeight: 1,
        margin: 0,
      }}>
        {copy}
      </h1>
    </div>
  );
}

function NotInPortfolio({ slug }: { slug: string }) {
  return (
    <div style={{ paddingTop: 48 }}>
      <p className="caps" style={{ marginBottom: 24, color: "var(--alert)" }}>
        Not in the portfolio
      </p>
      <h1 style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "clamp(38px, 6vw, 72px)", margin: 0 }}>
        "{slug}" is not a published case.
      </h1>
      <p style={{ marginTop: 16, maxWidth: "56ch", fontSize: 18, color: "var(--ink-2)" }}>
        Four cases are on file: <em>road-traffic</em>, <em>bpi-2017</em>, <em>sepsis</em>,{" "}
        <em>helpdesk</em>. Pick one from the right-hand rail.
      </p>
    </div>
  );
}

function BackendMissing({ slug }: { slug: string }) {
  return (
    <div style={{ paddingTop: 48 }}>
      <p className="caps" style={{ marginBottom: 24, color: "var(--alert)" }}>Backend has no match</p>
      <h1 style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "clamp(38px, 6vw, 72px)", margin: 0 }}>
        "{slug}" exists in the portfolio but not yet on the server.
      </h1>
      <p style={{ marginTop: 16, maxWidth: "56ch", fontSize: 18, color: "var(--ink-2)" }}>
        The authored copy is ready; the pre-computed analysis is missing. Run{" "}
        <code className="mono">uv run flowlens-seed</code> and refresh.
      </p>
    </div>
  );
}

function ErrorPanel({ err }: { err: unknown }) {
  const msg = err instanceof Error ? err.message : String(err ?? "unknown error");
  return (
    <div style={{ paddingTop: 48 }}>
      <p className="caps" style={{ marginBottom: 24, color: "var(--alert)" }}>
        The backend refused the request
      </p>
      <h1 style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "clamp(38px, 6vw, 72px)", margin: 0 }}>
        A gap in the archive.
      </h1>
      <p style={{ marginTop: 16, maxWidth: "56ch", fontSize: 18, color: "var(--ink-2)" }}>
        {msg}. Check that the backend is running and{" "}
        <code className="mono">VITE_API_BASE_URL</code> points at it.
      </p>
    </div>
  );
}
